import { calendarDaysBetween, toLocalDate } from '@pazportal/ui';

/** Format cents to currency string like $8,400.00 */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/** Format cents to compact like "$94.2k" */
export function formatMrrCompact(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  return `$${dollars.toFixed(0)}`;
}

/** Format ISO date string to relative time ("2 hours ago") */
export function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/** Calendar days from today to a date (positive = days remaining) */
export function daysUntil(dateStr: string | null): number | null {
  return calendarDaysBetween(new Date(), dateStr);
}

/** Format solution_type enum to human label */
export function formatSolutionType(type: string): string {
  const labels: Record<string, string> = {
    managed_network: 'Managed Network Solutions',
    video_surveillance: 'Surveillance Solutions',
    access_control: 'Access Control',
    pro_av: 'Pro AV',
    digital_signage: 'Digital Signage',
    security_alarm: 'Security / Alarm',
    smart_robotics: 'Smart Robotics Solutions',
    keytracer: 'Security Solutions (KeyTracer)',
    it_cloud: 'IT & Cloud',
    pos_systems: 'Retail Solutions (POS)',
    conferencing: 'Conferencing Solutions',
    audio: 'Audio Solutions',
  };
  return labels[type] ?? type;
}

/** Format audit action to human-readable string */
export function formatAuditAction(action: string): string {
  const parts = action.split('.');
  if (parts.length === 2) {
    const table = parts[0].replace(/_/g, ' ');
    const verb = parts[1].toLowerCase();
    const verbMap: Record<string, string> = {
      insert: 'created',
      update: 'updated',
      delete: 'deleted',
    };
    return `${table} ${verbMap[verb] ?? verb}`;
  }
  return action.replace(/_/g, ' ');
}

/**
 * Format date to short display.
 *
 * A bare `YYYY-MM-DD` (a Postgres DATE, e.g. a trip's start_date) is a calendar
 * day, not an instant: `new Date('2025-10-01')` is UTC midnight, which shows as
 * Sep 30 anywhere west of Greenwich. Build it as a local date instead.
 * Timestamps keep their instant.
 */
export function formatDate(dateStr: string | null): string {
  const date = toLocalDate(dateStr);
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format date with time */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Human label for audit entity_type. Never show the raw table name as the only copy. */
export function formatEntityType(type: string | null | undefined): string {
  if (!type) return '\u2014';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function summarizeAuditState(
  state: unknown,
): Array<{ label: string; value: string }> {
  if (!state || typeof state !== 'object') return [];
  return Object.entries(state as Record<string, unknown>)
    .filter(([key]) => key !== 'id' && key !== 'signature' && !key.endsWith('_id'))
    .map(([key, value]) => ({
      label: key.replace(/_/g, ' '),
      value: formatAuditField(value),
    }))
    .filter((row) => row.value !== '')
    .slice(0, 12);
}

function formatAuditField(value: unknown): string {
  if (value == null) return '\u2014';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (UUID_RE.test(value)) return '';
    if (value.length > 80) return `${value.slice(0, 77)}\u2026`;
    return value;
  }
  if (Array.isArray(value)) return value.length ? `${value.length} items` : 'None';
  return 'Updated';
}

/** Format SLA tier to display label */
export function formatSlaTier(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
